import React, { createContext, PropsWithChildren, ReactElement, useContext, useState } from 'react';
import { Outlet } from 'react-router-dom';

interface TargetingContextState<T extends string> {
  updatedCategories: T[];
  lastUpdatedCategory?: T;
  addUpdatedCategory: (category: T) => void;
  resetContext: () => void;
}

const TargetingContext = createContext<TargetingContextState<string>>({
  updatedCategories: [],
  lastUpdatedCategory: undefined,
  addUpdatedCategory: undefined,
  resetContext: undefined,
});

interface TargetingContextProviderProps {
  clearLastUpdatedCategory?: boolean;
}

export const TargetingContextProvider: React.FC<PropsWithChildren<TargetingContextProviderProps>> = ({
  clearLastUpdatedCategory,
}): ReactElement => {
  const [lastUpdatedCategory, setLastUpdatedCategory] = useState('');
  const [updatedCategories, setUpdatedCategories] = useState<string[]>([]);

  const handleAddUpdatedCategory = (category: string): void => {
    setLastUpdatedCategory(category);
    setUpdatedCategories((prevState) => Array.from(new Set([...prevState, category])).filter(Boolean));
  };

  const handleOnResetContext = (): void => {
    setLastUpdatedCategory('');
    setUpdatedCategories([]);
  };

  return (
    <TargetingContext.Provider
      value={{
        lastUpdatedCategory: clearLastUpdatedCategory ? '' : lastUpdatedCategory,
        updatedCategories,
        addUpdatedCategory: handleAddUpdatedCategory,
        resetContext: handleOnResetContext,
      }}>
      <Outlet />
    </TargetingContext.Provider>
  );
};

export const useTargetingContextProvider = <T extends string>(): TargetingContextState<T> =>
  useContext(TargetingContext) as TargetingContextState<T>;
